import { useMemo } from 'react';

import type { BaseRowData } from '@adv-frontend/data-provider';
import { Stub, Cell } from '@direct-frontend/components';
import cn from 'classnames';

import type { ColumnCellWrapperTemplateProps, GridCell } from '../../../types';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `CellStub`.
 */
type CellStubProps<RowData extends BaseRowData> = {
    /**
     * CSS-класс, который будет добавлен к контейнеру ячейки.
     */
    className?: string;
    /**
     * Инстанс ячейки грида.
     */
    cell: GridCell<RowData>;
    /**
     * Высота ячейки.
     */
    height: number;
};

function defaultRenderCellStubWrapper({
    children,
    className,
    style,
    'data-testid': dataTestId,
}: ColumnCellWrapperTemplateProps) {
    return (
        <div className={className} style={style} data-testid={dataTestId}>
            {children}
        </div>
    );
}

function defaultRenderCellStub() {
    return (
        <Cell size="s">
            <Stub height={16} width="100%" />
        </Cell>
    );
}

/**
 * Компонент-стаб ячейки грида.
 */
export function CellStub<RowData extends BaseRowData>({ className, cell, height }: CellStubProps<RowData>) {
    const width = cell.column.getSize();
    const inlineStyles = useMemo(() => ({ width, height }), [width, height]);

    const renderStub = cell.column.columnDef.renderStub ?? defaultRenderCellStub;
    const renderWrapper = cell.column.columnDef.renderCellWrapper ?? defaultRenderCellStubWrapper;
    const hasRightBorder = cell.column.columnDef.hasDelimiter;

    return renderWrapper({
        children: renderStub({ ...cell.getContext() }),
        className: cn(styles.cell, className, hasRightBorder ? styles.rightBorder : undefined),
        style: inlineStyles,
        'data-testid': `Grid.CellStub-${cell.id}`,
    });
}
